import { FormikProps } from 'formik'
import { useMemo } from 'react'
import ReactDragListView from 'react-drag-listview/lib/index.js'
import { useTranslation } from 'react-i18next'
import { AccordionBody, AccordionHeader, AccordionItem, Button, Card, CardHeader, Col, Label, Row, UncontrolledAccordion } from 'reactstrap'
import { handleImagesOrder } from '../../../../api/images/helpers'
import images from '../../../../api/images/hooks'
import { offerModalFields } from '../../../../common/forms/generalRealEstate/fields'
import Loader from '../../../Common/Loader'
import { useBlobUrls } from '../../../Hooks/useBlobUrl'
import { OfferSettingsFormValues } from '../types'

interface OfferImagesOrderProps {
    formik: FormikProps<OfferSettingsFormValues>
}

const OfferImagesOrder = (props: OfferImagesOrderProps) => {
    const { t } = useTranslation()
    const { formik } = props

    const offerPhotoOrder = formik.values.offer_photo_order

    const imagesIds: { id: number }[] = useMemo(() => {
        if (!offerPhotoOrder) return []

        return offerPhotoOrder.split(',').map((id: string) => ({ id: +id }))
    }, [offerPhotoOrder])

    const imageUrlQueries = images.GetImagesByUrl.useQueries(imagesIds, '')
    const blobUrls = useBlobUrls(imageUrlQueries)

    const dragProps = useMemo(
        () => ({
            onDragEnd(fromIndex: number, toIndex: number) {
                const updatedImages = handleImagesOrder(fromIndex, toIndex, imagesIds)
                const updatedPhotoOrder = updatedImages.map((img) => img.id).join(',')
                formik.setFieldValue(offerModalFields.OFFER_PHOTO_ORDER, updatedPhotoOrder)
            },
            nodeSelector: '.drag-node',
            handleSelector: '.mdi-menu',
            lineClassName: 'dmn-photo-drag',
        }),
        [imagesIds, formik]
    )

    const handleImageRemove = (photoId: string) => {
        const updatedImages = imagesIds.filter((img) => img.id.toString() !== photoId)
        const updatedPhotoOrder = updatedImages.map((img) => img.id).join(',')

        formik.setFieldValue(offerModalFields.OFFER_PHOTO_ORDER, updatedPhotoOrder)
    }

    const imageGallery = (
        <Row className="dmn-drag-row">
            <ReactDragListView {...dragProps}>
                {imagesIds.map((image, i: number) => (
                    <Col xs={6} sm={4} className="drag-node" key={image.id}>
                        <Card>
                            <CardHeader className="bg-dark border-0 px-2 py-1 d-flex align-items-center justify-content-between">
                                <Button color="dark" className="btn-icon" style={{ cursor: 'move' }} onClick={(e) => e.preventDefault()}>
                                    <i className="mdi mdi-menu fs-19" />
                                </Button>
                                <Button className="btn-close btn-close-white" onClick={() => handleImageRemove(image.id.toString())}></Button>
                            </CardHeader>
                            <div className="card-img" style={{ position: 'relative' }}>
                                {imageUrlQueries[i]?.isPending ? <Loader /> : <img className="img-fluid" src={blobUrls[i]} alt="Card cap" />}
                            </div>
                        </Card>
                    </Col>
                ))}
            </ReactDragListView>
        </Row>
    )

    return (
        <Row>
            <Col sm={12}>
                <UncontrolledAccordion toggle={() => null}>
                    <AccordionItem className="mb-4">
                        <AccordionHeader className="accordion-header d-flex justify-content-between align-items-center" targetId="1">
                            <Label className="m-0">{t('modal.offerPrintSettings.select.photos.title')}</Label>
                        </AccordionHeader>
                        <AccordionBody accordionId="1">
                            <Row className="mt-4">
                                <Col sm={12} className="fw-light text-secondary">
                                    {!offerPhotoOrder ? t('modal.offerPrintSettings.select.photos.message') : imageGallery}
                                </Col>
                            </Row>
                        </AccordionBody>
                    </AccordionItem>
                </UncontrolledAccordion>
            </Col>
        </Row>
    )
}

export default OfferImagesOrder
